import React, { useRef, useEffect, useState } from 'react';
import type { NormalizedIdeaCardData } from '@/lib/idea-card-types';
import { IdeaCard } from './IdeaCards/IdeaCard';
import { getIdeaCardPresentation } from './IdeaCards/presentation';
import { useTranslation } from 'react-i18next';
import { useScrollOverlay } from '@/hooks/useScrollOverlay';
import { useArrowNavigation } from '@/hooks/useArrowNavigation';
import { useViewportIntersection } from '@/hooks/useViewportIntersection';
import { ScrollArrowButton } from './ScrollArrowButton';
import { DragOverlay } from './DragOverlay';

const COMPACT_HORIZONTAL_CARD_MAX_CHARS = 180;
const COMPACT_HORIZONTAL_CARD_MAX_LINES = 5;
const COMPACT_HORIZONTAL_CARD_MAX_SECTIONS = 2;

const getCompactBodyMetrics = (card: NormalizedIdeaCardData) => {
  if (card.preview) {
    return {
      totalChars: Number.POSITIVE_INFINITY,
      totalLines: Number.POSITIVE_INFINITY,
      sectionCount: Number.POSITIVE_INFINITY,
    };
  }

  if (card.bodyModel === 'sectioned_horizontal') {
    const sectionContent = card.displayBodySections
      .map((section) => `${section.displayLabel || section.label || ''}\n${section.displayContent || section.content}`)
      .join('\n\n');

    return {
      totalChars: sectionContent.trim().length,
      totalLines: sectionContent
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean).length,
      sectionCount: card.displayBodySections.length,
    };
  }

  const body = card.displayBody || '';

  return {
    totalChars: body.trim().length,
    totalLines: body
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean).length,
    sectionCount: 0,
  };
};

export const isCompactHorizontalCard = (card: NormalizedIdeaCardData): boolean => {
  if (card.layout !== 'horizontal') return false;

  const metrics = getCompactBodyMetrics(card);

  return (
    metrics.totalChars <= COMPACT_HORIZONTAL_CARD_MAX_CHARS &&
    metrics.totalLines <= COMPACT_HORIZONTAL_CARD_MAX_LINES &&
    metrics.sectionCount <= COMPACT_HORIZONTAL_CARD_MAX_SECTIONS
  );
};

export const shouldUseDenseHorizontalCardLayout = (cards: NormalizedIdeaCardData[]): boolean =>
  cards.length >= 3 && cards.every(isCompactHorizontalCard);

const FULL_WIDTH_HORIZONTAL_PREVIEW_TYPES = new Set(['calendar', 'comparison_view']);

const shouldUseFullWidthHorizontalCardLayout = (cards: NormalizedIdeaCardData[]): boolean =>
  cards.length === 1 && Boolean(cards[0]?.preview?.type && FULL_WIDTH_HORIZONTAL_PREVIEW_TYPES.has(cards[0].preview.type));

const hasOverflow = (scrollSize: number, clientSize: number): boolean => scrollSize - clientSize > 1;

interface HorizontalCardGroupProps {
  cards: NormalizedIdeaCardData[];
  groupLabel?: string;
  markdownIdeaComponentsByDensity: {
    compact: React.ComponentProps<typeof IdeaCard>['markdownComponents'];
    relaxed: React.ComponentProps<typeof IdeaCard>['markdownComponents'];
  };
}

export const HorizontalCardGroup: React.FC<HorizontalCardGroupProps> = ({
  cards,
  groupLabel,
  markdownIdeaComponentsByDensity,
}) => {
  const { t } = useTranslation();
  const useDenseLayout = shouldUseDenseHorizontalCardLayout(cards);
  const useFullWidthLayout = shouldUseFullWidthHorizontalCardLayout(cards);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  // Show arrows only on desktop (md breakpoint, only after mounting)
  const showArrows = isDesktop && hasHorizontalOverflow;

  // Drag overlay state
  const { showOverlay, dismissOverlay } = useScrollOverlay({
    containerRef: carouselRef,
    storageKey: 'lema_horizontal_cards_scrolled',
    hasOverflow: hasHorizontalOverflow,
  });

  // Arrow navigation
  const {
    canScrollLeft,
    canScrollRight,
    scrollLeft: handleScrollLeft,
    scrollRight: handleScrollRight,
  } = useArrowNavigation({
    containerRef: carouselRef,
    onScroll: dismissOverlay,
  });

  // Track viewport intersection for dynamic arrow positioning
  const { arrowTopPosition } = useViewportIntersection(carouselRef);

  useEffect(() => {
    const updateOverflowState = () => {
      setHasHorizontalOverflow(
        Boolean(carouselRef.current && hasOverflow(carouselRef.current.scrollWidth, carouselRef.current.clientWidth))
      );
      setIsDesktop(window.innerWidth >= 768);
    };

    updateOverflowState();

    window.addEventListener('resize', updateOverflowState);

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        updateOverflowState();
      });

      if (carouselRef.current) {
        resizeObserver.observe(carouselRef.current);
      }

      if (trackRef.current) {
        resizeObserver.observe(trackRef.current);
      }

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateOverflowState);
      };
    }

    return () => {
      window.removeEventListener('resize', updateOverflowState);
    };
  }, [cards, useDenseLayout, useFullWidthLayout]);

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className="my-6 -mx-2">
      {groupLabel && (
        <p className="mb-3 px-2 text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300">
          {groupLabel}
        </p>
      )}
      {useFullWidthLayout ? (
        <div className="w-full px-2">
          <div className="w-full">
            <IdeaCard
              card={cards[0]}
              markdownComponents={markdownIdeaComponentsByDensity[getIdeaCardPresentation(cards[0].layout).density]}
              className="h-full w-full shadow-sm"
              bodyClassName="h-full"
            />
          </div>
        </div>
      ) : (
      <div className="relative">
        {/* Arrow buttons for desktop */}
        {showArrows && (
          <>
            <ScrollArrowButton
              direction="left"
              onClick={handleScrollLeft}
              disabled={!canScrollLeft}
              visible={canScrollLeft}
              ariaLabel={t('common.scroll_to_previous_cards') || 'Scroll to previous cards'}
              topPosition={arrowTopPosition}
            />
            <ScrollArrowButton
              direction="right"
              onClick={handleScrollRight}
              disabled={!canScrollRight}
              visible={canScrollRight}
              ariaLabel={t('common.scroll_to_next_cards') || 'Scroll to next cards'}
              topPosition={arrowTopPosition}
            />
          </>
        )}
        <div
          ref={carouselRef}
          className={`carousel-container w-full px-2 ${
            hasHorizontalOverflow
              ? 'overflow-x-auto overscroll-x-contain scrollbar-none'
              : 'overflow-x-hidden'
          }`}
        >
        {/* Drag overlay hint */}
        <DragOverlay show={showOverlay} isMobile={!isDesktop} />
        {useDenseLayout ? (
          <div
            ref={trackRef}
            className="grid min-w-max auto-cols-[240px] grid-flow-col grid-rows-3 items-start gap-4 pb-2 sm:auto-cols-[280px] md:auto-cols-[320px]"
          >
            {cards.map((card, index) => (
              <div
                key={`${groupLabel || 'horizontal-group'}-dense-${index}`}
                className="w-full self-start"
              >
                <IdeaCard
                  card={card}
                  markdownComponents={markdownIdeaComponentsByDensity[getIdeaCardPresentation(card.layout).density]}
                  className="shadow-sm"
                  bodyClassName="pb-3"
                />
              </div>
            ))}
          </div>
        ) : (
          <div
            ref={trackRef}
            className="inline-flex min-w-max gap-4 pb-2"
          >
            {cards.map((card, index) => (
                <div
                  key={`${groupLabel || 'horizontal-group'}-default-${index}`}
                  className="w-[240px] flex-shrink-0 overflow-visible sm:w-[280px] md:w-[320px]"
                >
                  <IdeaCard
                    card={card}
                    markdownComponents={markdownIdeaComponentsByDensity[getIdeaCardPresentation(card.layout).density]}
                    className="shadow-sm"
                    bodyClassName="pr-1"
                  />
                </div>
            ))}
          </div>
        )}
        </div>
      </div>
      )}
    </div>
  );
};
